import { z } from 'zod';
import {
  DEFAULT_RESERVATION_MINUTES,
  GUEST_SOURCES,
  RESERVATION_CHANNELS,
  RESERVATION_STATUSES,
  GUEST_STATUSES,
  MAX_COURSE,
  MAX_PRINT_COPIES,
  MIN_COURSE,
  ORDER_ITEM_STATUSES,
  ORDER_STATUSES,
  PAYMENT_METHODS,
  PRINT_JOB_CLAIM_LIMIT,
  PRINT_JOB_KINDS,
  PRINT_JOB_STATUSES,
  PRODUCTION_DESTINATIONS,
  STAFF_ROLES,
  TABLE_STATUSES,
  TAX_TYPES,
  TICKET_STATUSES,
} from './constants';

/**
 * Zod schemas for every API input. The Worker validates with these; the web app
 * reuses them for client-side form validation so the rules cannot drift.
 */

export const uuid = z.string().uuid();
export const cents = z.number().int();
export const positiveCents = z.number().int().min(0);
export const basisPoints = z.number().int().min(0).max(10_000);
export const courseSchema = z.number().int().min(MIN_COURSE).max(MAX_COURSE);
export const quantitySchema = z.number().int().min(1).max(999);
const trimmedString = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v.length === 0 ? null : v))
    .nullable()
    .optional();

/* --------------------------------- auth --------------------------------- */

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1).max(200),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(8).max(200),
});

/* --------------------------------- users -------------------------------- */

export const createUserSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  fullName: trimmedString(120),
  role: z.enum(STAFF_ROLES),
  password: z.string().min(8).max(200),
  pin: z
    .string()
    .regex(/^\d{4,6}$/)
    .nullable()
    .optional(),
  isActive: z.boolean().optional(),
});

export const updateUserSchema = createUserSchema.partial().extend({
  password: z.string().min(8).max(200).optional(),
});

/* -------------------------------- tables -------------------------------- */

export const createTableSchema = z.object({
  number: z.number().int().min(1).max(9999),
  name: trimmedString(60),
  capacity: z.number().int().min(1).max(60),
  section: optionalText(60),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
});

export const updateTableSchema = createTableSchema.partial().extend({
  status: z.enum(TABLE_STATUSES).optional(),
});

/* -------------------------------- orders -------------------------------- */

/** Optional guest-mix tags: null clears them, omitted leaves them as they are. */
const guestSource = z.enum(GUEST_SOURCES).nullable().optional();
const guestStatus = z.enum(GUEST_STATUSES).nullable().optional();

export const createOrderSchema = z.object({
  tableId: uuid,
  guestCount: z.number().int().min(1).max(100),
  notes: optionalText(500),
  guestSource,
  guestStatus,
});

export const updateOrderSchema = z.object({
  guestCount: z.number().int().min(1).max(100).optional(),
  /** Move the whole party to another table. The target must be free. */
  tableId: uuid.optional(),
  notes: optionalText(500),
  status: z.enum(ORDER_STATUSES).optional(),
  guestSource,
  guestStatus,
  /** Tip in cents. Kept off the bill, so it can be set on a closed order too. */
  tipCents: z.number().int().min(0).max(1_000_000_000).optional(),
});

export const orderItemModifierInput = z.object({
  modifierOptionId: uuid,
  quantity: z.number().int().min(1).max(20).default(1),
});

export const addOrderItemSchema = z.object({
  menuItemId: uuid,
  quantity: quantitySchema.default(1),
  course: courseSchema.default(1),
  notes: optionalText(300),
  seatNumber: z.number().int().min(1).max(60).nullable().optional(),
  modifiers: z.array(orderItemModifierInput).max(30).default([]),
});

export const addOrderItemsSchema = z.object({
  items: z.array(addOrderItemSchema).min(1).max(50),
});

export const updateOrderItemSchema = z.object({
  quantity: quantitySchema.optional(),
  course: courseSchema.optional(),
  notes: optionalText(300),
  seatNumber: z.number().int().min(1).max(60).nullable().optional(),
  modifiers: z.array(orderItemModifierInput).max(30).optional(),
});

export const cancelOrderItemSchema = z.object({
  reason: trimmedString(300),
});

/** Drag-and-drop reorder within a course: the new top-to-bottom item order. */
export const reorderOrderItemsSchema = z.object({
  course: courseSchema,
  itemIds: z.array(uuid).min(1).max(200),
});

/** Send unsent items. Omitting `itemIds` sends every draft item on the order. */
export const sendItemsSchema = z.object({
  itemIds: z.array(uuid).max(200).optional(),
  /** Optional per-item partial quantities; the remainder stays as a draft line. */
  quantities: z.record(uuid, quantitySchema).optional(),
  courses: z.array(courseSchema).max(MAX_COURSE).optional(),
});

export const paymentSchema = z.object({
  amountCents: z.number().int().min(1),
  method: z.enum(PAYMENT_METHODS),
  reference: optionalText(120),
  tenderedCents: z.number().int().min(0).nullable().optional(),
});

/** One line's worth of units a split payment is meant to cover. */
export const splitPaymentSelectionSchema = z.object({
  orderItemId: uuid,
  quantity: quantitySchema,
});

export const splitPaymentQuoteSchema = z.object({
  selections: z.array(splitPaymentSelectionSchema).min(1).max(100),
});

export const splitPaymentSchema = splitPaymentQuoteSchema.extend({
  method: z.enum(PAYMENT_METHODS),
  reference: optionalText(120),
  tenderedCents: z.number().int().min(0).nullable().optional(),
});

/**
 * A discount on the whole bill. PERCENT is in basis points (1000 = 10%) and
 * stops short of 100%: a fully comped table is an invitation, not a discount.
 * FIXED is an amount in cents. The Worker also checks it against the live subtotal.
 */
export const setDiscountSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('PERCENT'),
    value: z.number().int().min(1).max(9_999),
    reason: optionalText(200),
  }),
  z.object({
    type: z.literal('FIXED'),
    value: z.number().int().min(1),
    reason: optionalText(200),
  }),
]);

/**
 * A discount on one item. PERCENT is in basis points and, unlike the
 * bill-level discount, 100% (10,000bp) is allowed: that is how a single dish
 * goes on the house while still showing on the bill.
 */
export const setItemDiscountSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('PERCENT'),
    value: z.number().int().min(1).max(10_000),
    reason: optionalText(200),
  }),
  z.object({
    type: z.literal('FIXED'),
    value: z.number().int().min(1),
    reason: optionalText(200),
  }),
]);

/** Move whole lines, or part of their quantity, onto another table in one go. */
export const moveOrderItemsSchema = z.object({
  targetTableId: uuid,
  items: z
    .array(z.object({ orderItemId: uuid, quantity: quantitySchema.optional() }))
    .min(1)
    .max(200),
});

export const closeOrderSchema = z.object({
  force: z.boolean().optional(),
  /**
   * The house is paying. The bill is still raised, closed and kept, but it is
   * settled by invitation rather than by a payment, and the history screen
   * leaves it out of the takings.
   */
  isInvite: z.boolean().optional(),
});

/* ------------------------------ print queue ------------------------------- */

export const createPrintJobSchema = z.object({
  kind: z.enum(PRINT_JOB_KINDS).default('BILL'),
  copies: z.number().int().min(1).max(MAX_PRINT_COPIES).default(1),
  /** Print just one split payment's slice of the bill rather than the whole order. */
  paymentId: uuid.optional(),
});

export const claimPrintJobsSchema = z.object({
  /** Free text so a site with two tills can tell which one printed what. */
  stationName: z.string().trim().min(1).max(60).default('Cashier'),
  limit: z.number().int().min(1).max(PRINT_JOB_CLAIM_LIMIT).default(PRINT_JOB_CLAIM_LIMIT),
});

export const failPrintJobSchema = z.object({
  message: z.string().trim().max(300).optional(),
});

export const printJobQuerySchema = z.object({
  status: z.enum(PRINT_JOB_STATUSES).optional(),
  orderId: uuid.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(40),
});

/* --------------------------------- menu --------------------------------- */

export const createCategorySchema = z.object({
  name: trimmedString(80),
  parentId: uuid.nullable().optional(),
  description: optionalText(300),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
});

export const updateCategorySchema = createCategorySchema.partial();

export const reorderCategoriesSchema = z.object({
  items: z.array(z.object({ id: uuid, sortOrder: z.number().int().min(0) })).min(1),
});

export const createMenuItemSchema = z.object({
  categoryId: uuid,
  name: trimmedString(120),
  description: optionalText(500),
  priceCents: positiveCents,
  taxType: z.enum(TAX_TYPES),
  destination: z.enum(PRODUCTION_DESTINATIONS),
  sku: optionalText(60),
  unit: optionalText(30),
  imageUrl: optionalText(500),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
  prepMinutes: z.number().int().min(0).max(600).nullable().optional(),
  trackStock: z.boolean().optional(),
  /** Only meaningful while `trackStock` is on; ignored (and cleared server-side) otherwise. */
  stockQuantity: z.number().int().min(0).nullable().optional(),
  modifierGroupIds: z.array(uuid).max(20).optional(),
});

export const updateMenuItemSchema = createMenuItemSchema.partial();

export const createModifierGroupSchema = z.object({
  name: trimmedString(80),
  description: optionalText(300),
  isRequired: z.boolean().default(false),
  minSelections: z.number().int().min(0).max(20).default(0),
  maxSelections: z.number().int().min(1).max(20).nullable().optional(),
  allowQuantity: z.boolean().default(false),
  isActive: z.boolean().optional(),
});

export const updateModifierGroupSchema = createModifierGroupSchema.partial();

export const createModifierOptionSchema = z.object({
  name: trimmedString(80),
  priceDeltaCents: cents.default(0),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
});

export const updateModifierOptionSchema = createModifierOptionSchema.partial();

/* ------------------------------- production ------------------------------ */

export const updateTicketSchema = z.object({
  status: z.enum(TICKET_STATUSES),
});

export const updateTicketItemSchema = z.object({
  status: z.enum(ORDER_ITEM_STATUSES),
});

/* -------------------------------- settings ------------------------------- */

export const updateSettingsSchema = z.object({
  restaurantName: trimmedString(120).optional(),
  logoUrl: optionalText(500_000),
  addressLine1: optionalText(200),
  addressLine2: optionalText(200),
  phone: optionalText(60),
  email: optionalText(120),
  website: optionalText(200),
  taxNumber: optionalText(60),
  currency: z.string().trim().length(3).optional(),
  currencySymbol: z.string().trim().min(1).max(4).optional(),
  currencyDecimals: z.boolean().optional(),
  serviceChargeBp: basisPoints.optional(),
  foodTaxBp: basisPoints.optional(),
  alcoholTaxBp: basisPoints.optional(),
  receiptThankYouMessage: z.string().trim().min(1).max(600).optional(),
  receiptFooterNote: optionalText(300),
});

export const invoiceBuyerSchema = z.object({
  company: optionalText(200),
  name: optionalText(120),
  /** 10 digits, optionally a 3-digit branch suffix: 0316162487 or 0316162487-003. */
  taxCode: z
    .string()
    .trim()
    .regex(/^\d{10}(-\d{3})?$/, 'A Vietnamese tax code is 10 digits, optionally followed by -NNN')
    .nullable()
    .optional()
    .or(z.literal('')),
  address: optionalText(300),
  email: z.string().trim().email().max(120).nullable().optional().or(z.literal('')),
});
export type InvoiceBuyerInput = z.infer<typeof invoiceBuyerSchema>;

export const updateInvoicingSettingsSchema = z.object({
  enabled: z.boolean(),
  baseUrl: z
    .string()
    .trim()
    .max(200)
    .url()
    .refine((value) => value.startsWith('https://'), 'The base URL must use https')
    .optional()
    .or(z.literal('')),
  username: z.string().trim().max(120).optional(),
  /** Omitted or empty keeps the stored password. */
  password: z.string().max(200).optional(),
  taxCode: z.string().trim().max(40).optional(),
  pattern: z.string().trim().max(30).optional(),
  serial: z.string().trim().max(30).optional(),
  serviceChargeVatRate: z
    .string()
    .trim()
    .regex(/^-?\d{1,2}(\.\d{1,2})?$/, 'Enter a VAT rate such as 0, 8, 10 or -2')
    .optional(),
  defaultUnit: z.string().trim().min(1).max(30).optional(),
  includeServiceCharge: z.boolean().optional(),
});
export type UpdateInvoicingSettingsInput = z.infer<typeof updateInvoicingSettingsSchema>;

/* ------------------------------ reservations ------------------------------ */

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

export const createReservationSchema = z.object({
  guestName: trimmedString(120),
  phone: optionalText(40),
  partySize: z.number().int().min(1).max(100),
  reservedAt: z.string().datetime(),
  durationMinutes: z.number().int().min(15).max(600).default(DEFAULT_RESERVATION_MINUTES),
  tableId: uuid.nullable().optional(),
  channel: z.enum(RESERVATION_CHANNELS).default('PHONE'),
  guestSource,
  notes: optionalText(500),
});
export type CreateReservationInput = z.infer<typeof createReservationSchema>;

/** Every field optional; defaults are not re-applied on an edit. */
export const updateReservationSchema = z.object({
  guestName: trimmedString(120).optional(),
  phone: optionalText(40),
  partySize: z.number().int().min(1).max(100).optional(),
  reservedAt: z.string().datetime().optional(),
  durationMinutes: z.number().int().min(15).max(600).optional(),
  tableId: uuid.nullable().optional(),
  channel: z.enum(RESERVATION_CHANNELS).optional(),
  guestSource,
  notes: optionalText(500),
});
export type UpdateReservationInput = z.infer<typeof updateReservationSchema>;

/** Seated is reached only by seating the party, which opens their table. */
export const reservationStatusSchema = z.object({
  status: z.enum(RESERVATION_STATUSES).exclude(['SEATED']),
});

/** Seat on the booked table, or on another one chosen at the door. */
export const seatReservationSchema = z.object({ tableId: uuid.optional() });

export const reservationQuerySchema = z.object({ date: isoDate });

export const bookingAvailabilityQuerySchema = z.object({
  date: isoDate,
  partySize: z.coerce.number().int().min(1).max(50).default(2),
});

export const updateBookingSettingsSchema = z.object({
  enabled: z.boolean(),
  /** Omitted or empty keeps the saved token. */
  token: z.string().trim().max(500).optional(),
});
export type UpdateBookingSettingsInput = z.infer<typeof updateBookingSettingsSchema>;

/* -------------------------------- queries -------------------------------- */

export const orderHistoryQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  tableId: uuid.optional(),
  staffId: uuid.optional(),
  status: z.enum(ORDER_STATUSES).optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).optional(),
  /**
   * Narrow to the tables the house paid for, or to the paying ones only.
   * Accepts a real boolean as well as the string a query parameter carries.
   */
  isInvite: z
    .union([z.boolean(), z.enum(['true', 'false']).transform((value) => value === 'true')])
    .optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
});

/** The history filters without paging: an export takes every matching order. */
export const orderExportQuerySchema = orderHistoryQuerySchema.omit({ page: true, pageSize: true });

/**
 * The management reports all read the same window of orders.
 *
 * `tzOffsetMinutes` is the browser's own `getTimezoneOffset()`: day and hour
 * buckets have to be cut in the restaurant's local time, not UTC, or a late
 * service lands on tomorrow's takings.
 */
export const reportQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  tzOffsetMinutes: z.coerce.number().int().min(-840).max(840).default(0),
  /** Omitted: every menu item is listed, unsold ones included. */
  topItemsLimit: z.coerce.number().int().min(1).max(1000).optional(),
  /**
   * Count invited tables like any other settled order in every section. Off by
   * default, because what the house gave away is not takings.
   */
  includeInvited: z
    .union([z.boolean(), z.enum(['true', 'false']).transform((value) => value === 'true')])
    .default(false),
});

export const auditQuerySchema = z.object({
  entityType: z.string().max(60).optional(),
  entityId: uuid.optional(),
  userId: uuid.optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});

export type LoginInput = z.infer<typeof loginSchema>;
export type CreateOrderInput = z.infer<typeof createOrderSchema>;
export type AddOrderItemInput = z.infer<typeof addOrderItemSchema>;
export type UpdateOrderItemInput = z.infer<typeof updateOrderItemSchema>;
export type SendItemsInput = z.infer<typeof sendItemsSchema>;
export type ReorderOrderItemsInput = z.infer<typeof reorderOrderItemsSchema>;
export type PaymentInput = z.infer<typeof paymentSchema>;
export type MoveOrderItemsInput = z.infer<typeof moveOrderItemsSchema>;
export type SetItemDiscountInput = z.infer<typeof setItemDiscountSchema>;
export type SplitPaymentSelection = z.infer<typeof splitPaymentSelectionSchema>;
export type SplitPaymentQuoteInput = z.infer<typeof splitPaymentQuoteSchema>;
export type SplitPaymentInput = z.infer<typeof splitPaymentSchema>;
export type SetDiscountInput = z.infer<typeof setDiscountSchema>;
export type CreateMenuItemInput = z.infer<typeof createMenuItemSchema>;
export type CreateCategoryInput = z.infer<typeof createCategorySchema>;
export type UpdateSettingsInput = z.infer<typeof updateSettingsSchema>;
export type OrderHistoryQuery = z.infer<typeof orderHistoryQuerySchema>;
export type OrderExportQuery = z.infer<typeof orderExportQuerySchema>;
export type ReportQuery = z.infer<typeof reportQuerySchema>;
export type CreatePrintJobInput = z.infer<typeof createPrintJobSchema>;
export type ClaimPrintJobsInput = z.infer<typeof claimPrintJobsSchema>;
export type PrintJobQuery = z.infer<typeof printJobQuerySchema>;
